/**
 * Admin → Contacts.
 *
 * Winnie, 15 September: "Add Contacts as a new Admin nav item, alongside Users & Permissions,
 * Notifications, Audit Log, Settings, Transport Panel, and SMS", with the field list this form
 * follows exactly — contact number (auto-generated), name, legal name, ABN (validated with the
 * same checksum as everywhere else), address, phone, email, type (multi-select), bank account
 * name and number for suppliers paid through AP, and an Active/Inactive status.
 *
 * Two details that are decisions rather than layout:
 *
 * - The ABN is validated with `isValidAbn` from src/lib/abn — the same single copy the invoice
 *   reader and the deal forms use. Winnie's 13 Sep note on this was explicit: one ABN
 *   algorithm, not a second one.
 * - Looking an ABN up here records the GST status the register returned. That is the field that
 *   lets the registry answer the next lookup for this ABN without calling the ABR at all, so
 *   the button is worth pressing even when the name is already typed.
 *
 * The filters use "All", per the naming rule on the same tab.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Contact, ContactType, CONTACT_TYPES, contactsService, abnDigits } from '../../services/contactsService'
import { lookupAbn } from '../../services/buyerService'
import { isValidAbn, formatAbn } from '../../lib/abn'
import { toast } from '../Toast'

const EMPTY: Contact = {
  id: '',
  contact_number: null,
  name: '',
  legal_name: null,
  abn: null,
  address: null,
  phone: null,
  email: null,
  contact_type: [],
  bank_account_name: null,
  bank_account_number: null,
  status: 'Active',
  gst_registered: null,
  abn_status: null,
  abn_checked_at: null,
  notes: null,
}

export default function ContactsTab() {
  const [rows, setRows] = useState<Contact[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<'' | ContactType>('')
  const [statusFilter, setStatusFilter] = useState<'' | 'Active' | 'Inactive'>('')
  const [editing, setEditing] = useState<Contact | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setRows(await contactsService.list())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load contacts.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase()
    return rows.filter((c) => {
      if (typeFilter && !(c.contact_type || []).includes(typeFilter)) return false
      if (statusFilter && c.status !== statusFilter) return false
      if (!t) return true
      return [c.name, c.legal_name, c.abn, c.email, c.contact_number]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(t))
    })
  }, [rows, search, typeFilter, statusFilter])

  return (
    <>
      <p className="page-description">
        One record per counterparty — buyers, sellers, suppliers and dealers. The ABN lookup on
        the deal and invoice screens checks this registry before it calls the Australian Business
        Register, so a business recorded here does not need looking up, or re-typing, again.
      </p>

      <div className="admin-toolbar">
        <button className="abtn primary" onClick={() => setEditing({ ...EMPTY })}>
          + Add Contact
        </button>
        <input
          className="admin-search"
          placeholder="Search name, ABN, email or contact number…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="admin-input"
          style={{ maxWidth: 170 }}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as '' | ContactType)}
        >
          <option value="">All</option>
          {CONTACT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select
          className="admin-input"
          style={{ maxWidth: 150 }}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as '' | 'Active' | 'Inactive')}
        >
          <option value="">All</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      {error && <div className="admin-alert error">{error}</div>}

      {loading ? (
        <div className="admin-alert">Loading contacts…</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: '10%' }}>Number</th>
                <th style={{ width: '24%' }}>Name</th>
                <th style={{ width: '16%' }}>ABN</th>
                <th style={{ width: '18%' }}>Type</th>
                <th style={{ width: '22%' }}>Contact</th>
                <th style={{ width: '10%' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} className="clickable" onClick={() => setEditing(c)}>
                  <td className="admin-mono">{c.contact_number || '—'}</td>
                  <td>
                    <div className="admin-uname">{c.name}</div>
                    {c.legal_name && c.legal_name !== c.name && (
                      <div className="admin-uemail" style={{ marginTop: 2 }}>{c.legal_name}</div>
                    )}
                  </td>
                  <td className="admin-mono">{c.abn ? formatAbn(c.abn) : '—'}</td>
                  <td>{(c.contact_type || []).join(', ') || '—'}</td>
                  <td className="admin-uemail" style={{ marginTop: 0 }}>{c.email || c.phone || '—'}</td>
                  <td>
                    <span className={c.status === 'Active' ? 'admin-status-active' : 'admin-status-inactive'}>
                      {c.status}
                    </span>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ color: 'var(--txt3)' }}>
                    {rows.length === 0
                      ? 'No contacts yet. Add one, or let the registry fill itself — every successful ABN lookup on a deal or invoice offers to save the result here.'
                      : 'No contacts match.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ContactEditPanel
          contact={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            void load()
          }}
        />
      )}
    </>
  )
}

function ContactEditPanel({
  contact,
  onClose,
  onSaved,
}: {
  contact: Contact
  onClose: () => void
  onSaved: () => void
}) {
  const isAdd = !contact.id
  const [f, setF] = useState<Contact>({ ...contact })
  const [saving, setSaving] = useState(false)
  const [abnBusy, setAbnBusy] = useState(false)
  const [abnNote, setAbnNote] = useState<string>('')
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const set = (patch: Partial<Contact>) => setF((prev) => ({ ...prev, ...patch }))

  const toggleType = (t: ContactType) => {
    const have = f.contact_type || []
    set({ contact_type: have.includes(t) ? have.filter((x) => x !== t) : [...have, t] })
  }

  const checkAbn = async () => {
    const digits = abnDigits(f.abn)
    if (!digits) {
      setAbnNote('')
      return
    }
    if (!isValidAbn(digits)) {
      setAbnNote('That ABN does not pass the checksum.')
      return
    }
    setAbnBusy(true)
    try {
      const r = await lookupAbn(digits)
      setAbnNote(r.message)
      if (r.ok) {
        set({
          legal_name: f.legal_name || r.legalName || null,
          name: (f.name || '').trim() || r.legalName || '',
          /* Only a real ABR answer sets this; a registry answer already carries it. */
          gst_registered: r.gstRegistered ?? f.gst_registered ?? null,
          abn_status: r.abnStatus || f.abn_status || null,
        })
      }
    } catch (e) {
      setAbnNote(e instanceof Error ? e.message : 'The ABR could not be reached just now.')
    } finally {
      setAbnBusy(false)
    }
  }

  const save = async () => {
    const name = (f.name || '').trim()
    if (!name) {
      setProblem('A contact needs a name.')
      return
    }
    const digits = abnDigits(f.abn)
    if (digits && !isValidAbn(digits)) {
      setProblem('That ABN does not pass the checksum. Clear it or correct it.')
      return
    }
    setProblem(null)
    setSaving(true)
    try {
      const payload = {
        name,
        legal_name: f.legal_name,
        abn: digits || null,
        address: f.address,
        phone: f.phone,
        email: f.email,
        contact_type: f.contact_type || [],
        bank_account_name: f.bank_account_name,
        bank_account_number: f.bank_account_number,
        status: f.status,
        gst_registered: f.gst_registered,
        abn_status: f.abn_status,
        notes: f.notes,
      }
      if (isAdd) await contactsService.create(payload)
      else await contactsService.update(f.id, payload)
      toast.saved()
      onSaved()
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed'
      setProblem(msg)
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const field = (label: string, key: keyof Contact, placeholder?: string) => (
    <div className="admin-field" key={String(key)}>
      <label>{label}</label>
      <input
        className="admin-input"
        value={(f[key] as string) || ''}
        placeholder={placeholder}
        onChange={(e) => set({ [key]: e.target.value } as Partial<Contact>)}
      />
    </div>
  )

  return (
    <>
      <div className="admin-overlay" onClick={onClose} />
      <div className="admin-panel">
        <div className="admin-panel-header">
          <div className="admin-panel-title">{isAdd ? 'Add Contact' : `Contact ${f.contact_number || ''}`}</div>
          <button className="admin-panel-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="admin-panel-body">
          {problem && <div className="admin-alert error">{problem}</div>}

          {!isAdd && (
            <div className="admin-field">
              <label>Contact number</label>
              <input className="admin-input" value={f.contact_number || ''} disabled />
            </div>
          )}

          {field('Name', 'name', 'Trading name as you know them')}
          {field('Legal entity name', 'legal_name', 'Filled by the ABN lookup')}

          <div className="admin-field">
            <label>ABN</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="admin-input"
                value={f.abn || ''}
                placeholder="51 824 753 556"
                onChange={(e) => set({ abn: e.target.value })}
                onBlur={() => {
                  const d = abnDigits(f.abn)
                  if (d && !isValidAbn(d)) setAbnNote('That ABN does not pass the checksum.')
                }}
              />
              <button className="abtn" style={{ whiteSpace: 'nowrap' }} onClick={checkAbn} disabled={abnBusy}>
                {abnBusy ? 'Checking…' : 'Look up'}
              </button>
            </div>
            {abnNote && <p className="admin-hint" style={{ margin: '6px 0 0' }}>{abnNote}</p>}
            {f.gst_registered !== null && f.gst_registered !== undefined && (
              <p className="admin-hint" style={{ margin: '4px 0 0' }}>
                Recorded as {f.gst_registered ? 'GST registered' : 'not GST registered'} — this is what
                lets the registry answer the next lookup for this ABN without calling the register.
              </p>
            )}
          </div>

          <div className="admin-field">
            <label>Type</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 4 }}>
              {CONTACT_TYPES.map((t) => (
                <label key={t} className="admin-check-row" style={{ margin: 0 }}>
                  <input type="checkbox" checked={(f.contact_type || []).includes(t)} onChange={() => toggleType(t)} />
                  <span>{t}</span>
                </label>
              ))}
            </div>
          </div>

          {field('Address', 'address')}
          {field('Phone', 'phone')}
          {field('Email', 'email')}
          {field('Bank account name', 'bank_account_name', 'For suppliers paid through Payment Run (AP)')}
          {field('Bank account number', 'bank_account_number')}

          <div className="admin-field">
            <label>Status</label>
            <select
              className="admin-input"
              value={f.status}
              onChange={(e) => set({ status: e.target.value as 'Active' | 'Inactive' })}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="admin-panel-footer">
          <button className="abtn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="abtn primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </>
  )
}
